// BodyScanFlow — the photo-scan capture → process → result flow.
//
// CAPTURE LAYER (route decision, PR report): fallback capture via native
// photo inputs (capture="environment"), NOT the vendor's @3dlook/camera web
// component — its constructor is undocumented and WKWebView compat unverified
// (Fase 0-bis). The vendor's server-side quality checks still run: a failed
// scan returns per-photo errors ("The pose is wrong...", "The body is not
// full"...) rendered here as retake guidance. This component is the ONLY
// capture surface — swapping in the vendor widget later touches this file
// alone (the provider takes base64 photos, agnostic of who captured them).
//
// Photos: read into memory as data-URIs, sent to the edge fn, NEVER persisted
// (no storage upload, no DB column, no logs). State dies with the component.

import { useEffect, useRef, useState } from "react";
import { Loader2, Camera, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageProvider";
import { AgendaBottomSheet } from "@/components/agenda/CreateEventModal";
import { GroupedMeasurements } from "@/components/fitness/GroupedMeasurements";
import { supabase } from "@/integrations/supabase/client";
import { useBodyMeasurements, type BodyMeasurement } from "@/hooks/useBodyMeasurements";
import { getActiveScanProvider, type ScanResult } from "@/lib/bodyscan/provider";

type Step = "form" | "processing" | "failed" | "result";

interface BodyScanFlowProps {
  open: boolean;
  onClose: () => void;
  onSaved: (row: BodyMeasurement) => void;
}

const readAsDataUrl = (f: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error("photo read failed"));
    r.readAsDataURL(f);
  });

export default function BodyScanFlow({ open, onClose, onSaved }: BodyScanFlowProps) {
  const { t } = useLanguage();
  const { addScan, capture, userId, identityGeneration } = useBodyMeasurements();

  const [step, setStep] = useState<Step>("form");
  const [gender, setGender] = useState<"male" | "female">("male");
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [age, setAge] = useState("");
  const [frontPhoto, setFrontPhoto] = useState<string | null>(null);
  const [sidePhoto, setSidePhoto] = useState<string | null>(null);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [saving, setSaving] = useState(false);
  const prefilled = useRef(false);

  // Prefill gender/height from the profile (sex + height_cm exist there).
  useEffect(() => {
    if (!open || !userId || prefilled.current) return;
    const current = capture(); if (!current()) return;
    prefilled.current = true;
    void (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!current() || user?.id !== userId) return;
      const { data } = await supabase
        .from("user_profiles")
        .select("sex, height_cm, age")
        .eq("user_id", user.id)
        .maybeSingle();
      if (!current()) return;
      const p = data as { sex?: string | null; height_cm?: number | null; age?: number | null } | null;
      if (p?.sex === "female") setGender("female");
      if (p?.height_cm) setHeight(String(p.height_cm));
      if (p?.age) setAge(String(p.age));
    })();
  }, [open, userId, identityGeneration, capture]);

  if (!open) return null;

  const pickPhoto = (which: "front" | "side") => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const current = capture(); if (!current()) return;
    try {
      const dataUrl = await readAsDataUrl(f);
      if (!current()) return;
      if (which === "front") setFrontPhoto(dataUrl);
      else setSidePhoto(dataUrl);
    } catch (err) {
      console.error("[BodyScanFlow] photo read failed", err);
      toast.error(t("fitness.bodyScan.scan.error"));
    }
  };

  const heightNum = Number(height);
  const canStart = frontPhoto && sidePhoto && Number.isFinite(heightNum) && heightNum >= 145 && heightNum <= 220;

  const runScan = async () => {
    if (!canStart || !frontPhoto || !sidePhoto) return;
    const current = capture(); if (!current()) return;
    setStep("processing");
    try {
      const provider = getActiveScanProvider();
      const session = await provider.startScan({
        gender,
        heightCm: heightNum,
        weightKg: weight ? Number(weight) : null,
        age: age ? Number(age) : null,
        frontPhotoBase64: frontPhoto,
        sidePhotoBase64: sidePhoto,
      });
      if (!current()) return;
      const r = await provider.getResult(session);
      if (!current() || r.provider !== provider.id) return;
      setResult(r);
      setStep(r.status === "successful" ? "result" : "failed");
    } catch (err) {
      if (!current()) return;
      console.error("[BodyScanFlow] scan failed", err);
      toast.error(t("fitness.bodyScan.scan.error") + (err instanceof Error ? ` (${err.message})` : ""));
      setStep("form");
    }
  };

  const handleSave = async () => {
    if (!result || result.status !== "successful") return;
    const current = capture(); if (!current() || result.provider !== getActiveScanProvider().id) return;
    setSaving(true);
    try {
      const row = await addScan({
        ...result.measurements,
        source: "scan",
        scan_provider: result.provider,
        measured_at: new Date().toISOString(),
        extra_measurements: result.raw,
        // Phase-2 provenance (Body Progress / Future Body):
        ...(result.scanId ? { provider_scan_id: result.scanId } : {}),
        ...(result.modelUrl ? { model_3d_url: result.modelUrl } : {}),
      } as never);
      if (!current()) return;
      if (!row) {
        toast.error(t("fitness.bodyScan.saveError"));
        return;
      }
      toast.success(t("fitness.bodyScan.scan.saved"));
      onSaved(row);
      onClose();
    } finally {
      if (current()) setSaving(false);
    }
  };

  const inputStyle = { background: "hsl(222 18% 12%)", border: "1px solid hsl(222 18% 22%)", color: "white" } as const;
  const retakeErrors = (result?.errors || []).filter((e) => e.detail);

  const photoSlot = (which: "front" | "side", value: string | null) => (
    <label className="flex-1 rounded-xl p-3 text-center cursor-pointer" style={{ border: value ? "1px solid hsl(140 60% 45%)" : "1px dashed hsl(222 18% 28%)", background: "hsl(222 18% 10%)" }}>
      {value
        ? <CheckCircle2 size={18} className="mx-auto mb-1" style={{ color: "hsl(140 60% 55%)" }} />
        : <Camera size={18} className="mx-auto mb-1 text-muted-foreground" />}
      <p className="text-[11px] font-semibold text-white">{t(which === "front" ? "fitness.bodyScan.scan.frontPhoto" : "fitness.bodyScan.scan.sidePhoto")}</p>
      <p className="text-[9px] text-muted-foreground">{t(value ? "fitness.bodyScan.scan.retake" : "fitness.bodyScan.scan.choosePhoto")}</p>
      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pickPhoto(which)} />
    </label>
  );

  const footer =
    step === "form" ? (
      <button onClick={runScan} disabled={!canStart}
        className="w-full py-3 rounded-xl font-display text-[14px] font-semibold disabled:opacity-40"
        style={{ background: "hsl(187 72% 50%)", color: "hsl(222 38% 6%)" }}>
        {t("fitness.bodyScan.scan.start")}
      </button>
    ) : step === "result" ? (
      <button onClick={handleSave} disabled={saving}
        className="w-full py-3 rounded-xl font-display text-[14px] font-semibold"
        style={{ background: "hsl(187 72% 50%)", color: "hsl(222 38% 6%)" }}>
        {saving ? t("fitness.bodyScan.saving") : t("fitness.bodyScan.scan.save")}
      </button>
    ) : step === "failed" ? (
      <button onClick={() => setStep("form")}
        className="w-full py-3 rounded-xl font-display text-[14px] font-semibold"
        style={{ background: "hsl(187 72% 50%)", color: "hsl(222 38% 6%)" }}>
        {t("fitness.bodyScan.scan.tryAgain")}
      </button>
    ) : <div />;

  return (
    <AgendaBottomSheet open onClose={onClose} title={t("fitness.bodyScan.scan.title")} footer={footer}>
      {step === "form" && (
        <div className="space-y-4 pb-2">
          <div className="rounded-xl px-3 py-2.5" style={{ background: "hsl(187 72% 50% / 0.06)", border: "1px solid hsl(187 72% 50% / 0.18)" }}>
            <p className="text-[11px] font-semibold text-white mb-1">{t("fitness.bodyScan.scan.instructionsTitle")}</p>
            <ul className="text-[10px] text-muted-foreground space-y-0.5 list-disc pl-4">
              <li>{t("fitness.bodyScan.scan.instruction1")}</li>
              <li>{t("fitness.bodyScan.scan.instruction2")}</li>
              <li>{t("fitness.bodyScan.scan.instruction3")}</li>
              <li>{t("fitness.bodyScan.scan.instruction4")}</li>
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] text-muted-foreground">{t("fitness.bodyScan.scan.gender")}</span>
              <div className="flex gap-2 mt-1">
                {(["male", "female"] as const).map((g) => (
                  <button key={g} onClick={() => setGender(g)}
                    className="flex-1 py-2 rounded-lg text-xs font-semibold"
                    style={gender === g
                      ? { background: "hsl(187 72% 50% / 0.18)", color: "hsl(187 72% 60%)", border: "1px solid hsl(187 72% 50% / 0.4)" }
                      : { background: "hsl(222 18% 12%)", color: "hsl(222 10% 60%)", border: "1px solid hsl(222 18% 20%)" }}>
                    {t(g === "male" ? "fitness.bodyScan.scan.genderMale" : "fitness.bodyScan.scan.genderFemale")}
                  </button>
                ))}
              </div>
            </label>
            <label className="block">
              <span className="text-[11px] text-muted-foreground">{t("fitness.bodyScan.scan.height")}</span>
              <input type="number" inputMode="numeric" value={height} onChange={(e) => setHeight(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg text-sm" style={inputStyle} />
            </label>
            <label className="block">
              <span className="text-[11px] text-muted-foreground">{t("fitness.bodyScan.scan.weightOpt")}</span>
              <input type="number" inputMode="decimal" value={weight} onChange={(e) => setWeight(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg text-sm" style={inputStyle} />
            </label>
            <label className="block">
              <span className="text-[11px] text-muted-foreground">{t("fitness.bodyScan.scan.ageOpt")}</span>
              <input type="number" inputMode="numeric" value={age} onChange={(e) => setAge(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg text-sm" style={inputStyle} />
            </label>
          </div>
          <div className="flex gap-3">
            {photoSlot("front", frontPhoto)}
            {photoSlot("side", sidePhoto)}
          </div>
        </div>
      )}

      {step === "processing" && (
        <div className="text-center py-10">
          <Loader2 size={28} className="mx-auto mb-3 animate-spin" style={{ color: "hsl(187 72% 55%)" }} />
          <p className="text-sm text-white font-semibold">{t("fitness.bodyScan.scan.processing")}</p>
          <p className="text-[11px] text-muted-foreground mt-1">{t("fitness.bodyScan.scan.processingHint")}</p>
        </div>
      )}

      {step === "failed" && (
        <div className="py-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={18} style={{ color: "hsl(35 85% 55%)" }} />
            <p className="text-sm font-semibold text-white">{t("fitness.bodyScan.scan.failedTitle")}</p>
          </div>
          <div className="space-y-2">
            {retakeErrors.length > 0 ? retakeErrors.map((e, i) => (
              <div key={i} className="rounded-lg px-3 py-2 text-[12px]" style={{ background: "hsl(35 85% 55% / 0.08)", border: "1px solid hsl(35 85% 55% / 0.25)" }}>
                <span className="font-semibold" style={{ color: "hsl(35 85% 65%)" }}>
                  {e.error_source === "front_photo" ? t("fitness.bodyScan.scan.failedFront")
                    : e.error_source === "side_photo" ? t("fitness.bodyScan.scan.failedSide")
                    : t("fitness.bodyScan.scan.failedGeneric")}
                </span>
                <span className="text-foreground/70"> — {e.detail}</span>
              </div>
            )) : (
              <p className="text-[12px] text-muted-foreground">{t("fitness.bodyScan.scan.failedGeneric")}</p>
            )}
          </div>
        </div>
      )}

      {step === "result" && result && (
        <div className="space-y-4 pb-2">
          <p className="text-sm text-muted-foreground">{t(result.provider === 'mock' ? 'bodyHealth.scanMockNotice' : 'bodyHealthAction.partialSource')}</p>
          {result.composition && (
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: "fat_percentage", unit: "%" },
                { key: "estimated_weight", unit: "kg" },
                { key: "bmi", unit: "" },
                { key: "lean_body_mass", unit: "kg" },
                { key: "fat_body_mass", unit: "kg" },
                { key: "bmr", unit: "kcal" },
              ].map(({ key, unit }) => {
                const v = (result.composition as Record<string, unknown>)[key];
                if (typeof v !== "number") return null;
                return (
                  <div key={key} className="rounded-xl p-2.5 text-center" style={{ background: "hsl(222 18% 12%)", border: "1px solid hsl(187 72% 50% / 0.2)" }}>
                    <p className="font-display text-[16px] font-bold text-white">{Math.round(v * 10) / 10}<span className="text-[9px] text-muted-foreground ml-0.5">{unit}</span></p>
                    <p className="text-[8px] text-muted-foreground">{t(`fitness.bodyScan.m.comp.${key}`)}</p>
                  </div>
                );
              })}
            </div>
          )}
          <div>
            <p className="text-[11px] font-semibold text-white mb-2">{t("fitness.bodyScan.scan.allMeasures")}</p>
            <GroupedMeasurements raw={result.raw} />
          </div>
          {result.quota && typeof (result.quota as Record<string, unknown>).available === "number" && (
            <p className="text-[10px] text-muted-foreground text-center">
              {t("fitness.bodyScan.scan.quotaLeft")}: {String((result.quota as Record<string, unknown>).available)}
            </p>
          )}
        </div>
      )}
    </AgendaBottomSheet>
  );
}
